/**
 * JobClose AI · Slice 2T · Levi 1.0 Intelligence
 * ---------------------------------------------------
 * deriveLeviFacts(inputs) → { facts, context }
 *
 * Pure function. No React. No fetches. No timers. No LLM calls.
 *
 * Facts are independent booleans / small scalars. They may all be
 * simultaneously true. Do NOT collapse them into a linear stage. The
 * recommendation layer picks a "primary" but that never suppresses
 * other observations.
 *
 * DATA-HONESTY RULE
 * -----------------
 * Only emit a fact when the supporting data was actually supplied by
 * the host page. When an input is `undefined` we OMIT the related
 * facts entirely rather than assuming a confirmed negative.
 *
 * A confirmed empty array (e.g. `estimates: []`) is honest and does
 * yield facts (`estimates_count: 0`). A missing key (e.g. no
 * `workAreas` passed at all) yields no work-area facts.
 */

const _isFilledString = (v) =>
  typeof v === "string" && v.trim().length > 0;

const _lineNeedsPricing = (line) =>
  Boolean(line && (line.needs_pricing === true || line.needs_material_pricing === true));

const _pickPrimaryEstimate = (estimates) => {
  if (!Array.isArray(estimates) || estimates.length === 0) return null;
  // Sort by created_at desc; fall back to array order.
  const sorted = [...estimates].sort((a, b) => {
    const ax = String(a?.created_at || "");
    const bx = String(b?.created_at || "");
    if (ax > bx) return -1;
    if (ax < bx) return 1;
    return 0;
  });
  return sorted[0] || null;
};

const _ackIsStale = (est) => {
  // Only report staleness when supporting data actually exists.
  // Requires BOTH an ack (with acknowledged_at) AND the estimate to have
  // been updated *after* that ack. Otherwise "stale" is unknown.
  const ack = est && est.double_check_ack;
  const ackedAt = ack && ack.acknowledged_at;
  const updatedAt = est && est.updated_at;
  if (!ackedAt || !updatedAt) return null;
  return String(updatedAt) > String(ackedAt);
};

const _proposalIsShared = (p) => {
  const s = p && p.sharing && p.sharing.state;
  return s === "shared" || s === "approved";
};

const _summarizeProposals = (proposals) =>
  (proposals || []).map((p) => ({
    id: p?.id || null,
    status: p?.status || null,
    sharing_state: (p && p.sharing && p.sharing.state) || "private",
    updated_at: p?.updated_at || null,
  }));

const _summarizeContracts = (contracts) =>
  (contracts || []).map((c) => ({
    id: c?.id || null,
    status: c?.status || null,
    sharing_state: (c && c.sharing && c.sharing.state) || "private",
    updated_at: c?.updated_at || null,
  }));

const _summarizeInvoices = (invoices) =>
  (invoices || []).map((i) => ({
    id: i?.id || null,
    invoice_type: i?.invoice_type || null,
    payment_status: i?.payment_status || null,
    updated_at: i?.updated_at || null,
  }));

/**
 * Compute Levi facts for a project workspace or a full-project intake.
 *
 * @param {object}  inputs
 * @param {string}  inputs.context             - "project_workspace" | "full_project_intake"
 * @param {object}  [inputs.project]           - full project row (workspace) OR a shallow intake form snapshot (intake).
 * @param {Array}   [inputs.estimates]         - workspace only
 * @param {Array}   [inputs.proposals]         - workspace only
 * @param {Array}   [inputs.contracts]         - workspace only
 * @param {Array}   [inputs.invoices]          - workspace only
 * @param {Array}   [inputs.workOrders]        - workspace only
 * @param {Array}   [inputs.closeoutPackets]   - workspace only
 * @param {boolean} [inputs.intakeDraftProjectExists] - intake only
 * @returns {object} facts
 */
export function deriveLeviFacts(inputs) {
  const {
    context = "project_workspace",
    project,
    estimates,
    proposals,
    contracts,
    invoices,
    workOrders,
    closeoutPackets,
    intakeDraftProjectExists,
  } = inputs || {};

  const facts = { context };

  // ---------- Customer identity (workspace + intake) ----------
  if (project) {
    facts.customer_name_present = _isFilledString(project.customer_name);
    facts.customer_email_present = _isFilledString(project.customer_email);
    facts.customer_phone_present = _isFilledString(project.customer_phone);
    facts.job_address_present = _isFilledString(project.job_address);
    facts.job_type_present = _isFilledString(project.job_type);

    // Narrative signals.
    facts.job_description_present = _isFilledString(project.job_description);
    facts.typed_notes_present = _isFilledString(project.typed_notes);
    facts.voice_transcript_present = _isFilledString(project.voice_transcript);
    facts.measurements_text_present = _isFilledString(project.measurements);
    facts.material_notes_present = _isFilledString(project.material_notes);
    facts.labor_notes_present = _isFilledString(project.labor_notes);
    facts.special_conditions_present = _isFilledString(project.special_conditions);
    facts.customer_concerns_present = _isFilledString(project.customer_concerns);

    // Photos on the project record itself (URL array). Photos loaded via
    // child components (`PhotoUploader`) are NOT visible from here, so we
    // only report on the `project.photos` array shape.
    if (Array.isArray(project.photos)) {
      facts.photos_count = project.photos.length;
      facts.photos_present = project.photos.length > 0;
    }

    // Project lifecycle (never invented — mirrors the stored value).
    if (typeof project.status === "string") {
      facts.project_status = project.status;
      facts.project_is_draft = project.status === "draft";
      facts.project_is_closed = project.status === "closed";
    }
  }

  // ---------- Estimates ----------
  if (Array.isArray(estimates)) {
    facts.estimates_count = estimates.length;
    const primary = _pickPrimaryEstimate(estimates);
    if (primary) {
      facts.primary_estimate_id = primary.id || null;
      const lines = Array.isArray(primary.line_items) ? primary.line_items : [];
      facts.estimate_has_line_items = lines.length > 0;
      facts.estimate_line_items_count = lines.length;

      const unpriced = lines.filter(_lineNeedsPricing);
      facts.estimate_unpriced_lines_count = unpriced.length;
      facts.estimate_unpriced_line_names = unpriced
        .slice(0, 3)
        .map((l) => (l && (l.name || l.description) ? String(l.name || l.description) : ""))
        .filter(Boolean);

      const dc = primary.double_check;
      const dcRan = Boolean(dc && (dc.findings || dc.red_count !== undefined));
      facts.estimate_double_check_ran = dcRan;
      if (dcRan) {
        facts.estimate_double_check_red_count = Number(dc.red_count || 0);
        facts.estimate_double_check_yellow_count = Number(dc.yellow_count || 0);
      }

      const ack = primary.double_check_ack;
      facts.estimate_double_check_ack_present = Boolean(ack && ack.acknowledged_at);

      const stale = _ackIsStale(primary);
      if (stale !== null) facts.estimate_double_check_stale = stale;
    } else {
      facts.primary_estimate_id = null;
      facts.estimate_has_line_items = false;
      facts.estimate_line_items_count = 0;
      facts.estimate_unpriced_lines_count = 0;
      facts.estimate_double_check_ran = false;
      facts.estimate_double_check_ack_present = false;
    }
  }

  // ---------- Proposals (per document, never collapsed) ----------
  if (Array.isArray(proposals)) {
    const anyShared = proposals.some(_proposalIsShared);
    const anyApproved = proposals.some((p) => p && (p.status === "approved" || (p.sharing && p.sharing.state === "approved")));
    const anyDeclined = proposals.some((p) => p && p.status === "declined");
    facts.proposals_count = proposals.length;
    facts.proposals_any_shared = anyShared;
    facts.proposals_any_approved = anyApproved;
    facts.proposals_any_declined = anyDeclined;
    facts.proposals_awaiting_response = anyShared && !anyApproved && !anyDeclined;
    facts.proposals_summary = _summarizeProposals(proposals);
  }

  // ---------- Contracts ----------
  if (Array.isArray(contracts)) {
    const anyAwaiting = contracts.some((c) => c && c.status && c.status !== "signed" && c.status !== "fully_executed" && c.status !== "canceled");
    const anySigned = contracts.some((c) => c && (c.status === "signed" || c.status === "fully_executed"));
    facts.contracts_count = contracts.length;
    facts.contracts_any_awaiting_signature = anyAwaiting;
    facts.contracts_any_signed = anySigned;
    facts.contracts_summary = _summarizeContracts(contracts);
  }

  // ---------- Invoices ----------
  if (Array.isArray(invoices)) {
    facts.invoices_count = invoices.length;
    const anyDue = invoices.some((i) => i && i.payment_status && i.payment_status !== "paid");
    const allPaid = invoices.length > 0 && invoices.every((i) => i && i.payment_status === "paid");
    facts.invoices_any_payment_due = anyDue;
    facts.invoices_all_paid = allPaid;
    facts.invoices_summary = _summarizeInvoices(invoices);
  }

  // ---------- Work Orders (presence only) ----------
  if (Array.isArray(workOrders)) {
    facts.work_orders_count = workOrders.length;
    facts.work_orders_any_open = workOrders.some((w) => w && w.status && w.status !== "closed" && w.status !== "canceled");
  }

  // ---------- Closeout Packets ----------
  if (Array.isArray(closeoutPackets)) {
    facts.closeout_packet_count = closeoutPackets.length;
    facts.closeout_packet_generated = closeoutPackets.length > 0;
  }

  // ---------- Customer document access (per document, from what is present) ----------
  const openDocs = [];
  let anyCustomerFacing = false;
  const collectShareState = (kind, list) => {
    (list || []).forEach((doc) => {
      const state = (doc && doc.sharing && doc.sharing.state) || "private";
      if (state && state !== "private") {
        anyCustomerFacing = true;
        openDocs.push({
          kind,
          id: doc?.id || null,
          sharing_state: state,
        });
      }
    });
  };
  if (Array.isArray(proposals)) collectShareState("proposal", proposals);
  if (Array.isArray(contracts)) collectShareState("contract", contracts);
  if (Array.isArray(closeoutPackets)) collectShareState("closeout", closeoutPackets);
  if (Array.isArray(proposals) || Array.isArray(contracts) || Array.isArray(closeoutPackets)) {
    // Only emit these facts when we actually looked at some collection.
    facts.customer_access_open_docs = openDocs;
    facts.customer_access_no_docs_shared = !anyCustomerFacing;
  }

  // ---------- Intake-only helpers ----------
  if (context === "full_project_intake") {
    facts.intake_customer_ready =
      facts.customer_name_present === true &&
      (facts.customer_email_present === true || facts.customer_phone_present === true);
    facts.intake_has_any_narrative = Boolean(
      facts.job_description_present ||
      facts.typed_notes_present ||
      facts.voice_transcript_present ||
      facts.measurements_text_present ||
      facts.material_notes_present ||
      facts.labor_notes_present,
    );
    if (typeof intakeDraftProjectExists === "boolean") {
      facts.intake_draft_project_present = intakeDraftProjectExists;
    }
  }

  return facts;
}

export default deriveLeviFacts;
